import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaShoppingCart, 
  FaCreditCard,
  FaBox,
  FaChartLine,
  FaShieldAlt,
  FaMobile,
  FaUsers,
  FaStar,
  FaQuoteLeft,
  FaStore,
  FaGlobe,
  FaHeadset,
  FaRocket,
  FaCogs,
  FaTruck,
  FaPercent,
  FaSearch,
  FaSyncAlt
} from 'react-icons/fa';
import { SiShopify, SiWoocommerce, SiMagento, SiStripe, SiPaypal, SiBigcommerce, SiSquare, SiAmazon, SiApple } from 'react-icons/si';
import { serviceSeoData, seoImage } from '../../config/pageSeoConfig';
import './EcommerceSolutions.scss';

const EcommerceSolutions: React.FC = () => {
  // SEO constants from config
  const seoTitle = serviceSeoData.ecommerce.title;
  const seoDescription = serviceSeoData.ecommerce.description;
  const seoKeywords = serviceSeoData.ecommerce.keywords;
  const seoUrl = serviceSeoData.ecommerce.url;
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "E-commerce Development Services",
    "description": seoDescription,
    "provider": {
      "@type": "Organization",
      "name": "App Developer Pro",
      "url": "https://appdeveloperpro.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://appdeveloperpro.com/images/team-at-work-app-developer-pro.jpg"
      }
    },
    "areaServed": "Worldwide",
    "serviceType": "E-commerce Development, Online Store Development, Shopify, WooCommerce, Magento, Payment Integration, Mobile Commerce, Analytics, Marketplace, B2B, B2C",
    "url": seoUrl,
    "image": seoImage
  };
  const features = [
    {
      icon: FaStore,
      title: 'Custom Online Stores',
      description: 'Fully customized e-commerce websites tailored to your brand and business needs'
    },
    {
      icon: FaCreditCard,
      title: 'Payment Integration',
      description: 'Secure payment gateways supporting multiple payment methods and currencies'
    },
    {
      icon: FaBox,
      title: 'Inventory Management',
      description: 'Advanced inventory tracking, stock alerts, and automated reordering systems'
    },
    {
      icon: FaChartLine,
      title: 'Analytics & Reporting',
      description: 'Comprehensive sales analytics and business intelligence dashboards'
    },
    {
      icon: FaShieldAlt,
      title: 'Security & Compliance',
      description: 'PCI DSS compliance, SSL certificates, and advanced security measures'
    },
    {
      icon: FaMobile,
      title: 'Mobile Commerce',
      description: 'Mobile-optimized shopping experiences and progressive web apps'
    }
  ];

  const platforms = [
    { icon: SiShopify, name: 'Shopify', color: '#96bf48', description: 'Leading e-commerce platform' },
    { icon: SiWoocommerce, name: 'WooCommerce', color: '#96588a', description: 'WordPress e-commerce plugin' },
    { icon: SiMagento, name: 'Magento', color: '#ee672f', description: 'Enterprise e-commerce solution' },
    { icon: FaShoppingCart, name: 'Custom Solutions', color: '#0EA5E9', description: 'Bespoke e-commerce development' },
    { icon: SiStripe, name: 'Stripe', color: '#635bff', description: 'Payment processing' },
    { icon: SiPaypal, name: 'PayPal', color: '#0070ba', description: 'Global payment solution' },
    { icon: SiBigcommerce, name: 'BigCommerce', color: '#121118', description: 'Enterprise e-commerce platform' },
    { icon: SiSquare, name: 'Square', color: '#3E4348', description: 'Point of sale & payments' },
    { icon: SiAmazon, name: 'Amazon Pay', color: '#FF9900', description: 'Amazon payment gateway' },
    { icon: SiApple, name: 'Apple Pay', color: '#000000', description: 'Mobile payment solution' }
  ];

  const benefits = [
    {
      icon: FaRocket,
      title: 'Increased Sales',
      description: 'Optimized checkout process and user experience to maximize conversions'
    },
    {
      icon: FaGlobe,
      title: 'Global Reach',
      description: 'Multi-currency support and international shipping capabilities'
    },
    {
      icon: FaHeadset,
      title: 'Customer Support',
      description: 'Integrated customer service tools and live chat functionality'
    },
    {
      icon: FaCogs,
      title: 'Easy Management',
      description: 'Intuitive admin dashboard for effortless product and order management'
    },
    {
      icon: FaTruck,
      title: 'Smart Shipping',
      description: 'Automated shipping calculations and real-time tracking integration'
    },
    {
      icon: FaPercent,
      title: 'Marketing Tools',
      description: 'Built-in discount codes, promotions, and email marketing capabilities'
    },
    {
      icon: FaSearch,
      title: 'SEO Optimized',
      description: 'Search engine friendly architecture for better product visibility'
    },
    {
      icon: FaSyncAlt,
      title: 'Auto Updates',
      description: 'Automatic platform updates and security patches for peace of mind'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Business Analysis',
      description: 'Understanding your products, target market, and business objectives'
    },
    {
      step: '02',
      title: 'Platform Selection',
      description: 'Choosing the best e-commerce platform for your specific needs'
    },
    {
      step: '03',
      title: 'Design & Development',
      description: 'Creating a beautiful, functional online store with seamless user experience'
    },
    {
      step: '04',
      title: 'Testing & Launch',
      description: 'Thorough testing of all functionality before going live'
    }
  ];

  const completedProjects = [
    {
      title: 'Fashion E-commerce Store',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'Shopify', icon: SiShopify, color: '#96bf48' },
        { name: 'Stripe', icon: SiStripe, color: '#635bff' },
        { name: 'PayPal', icon: SiPaypal, color: '#0070ba' }
      ],
      description: 'Custom Shopify store with 2,000+ products, subscription boxes, and loyalty program',
      metrics: {
        products: '2K+',
        orders: '5K/mo',
        revenue: '$500K/mo'
      }
    },
    {
      title: 'Electronics Marketplace',
      image: 'https://images.unsplash.com/photo-1468495244123-6c6c332eeece?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'WooCommerce', icon: SiWoocommerce, color: '#96588a' },
        { name: 'Square', icon: SiSquare, color: '#3E4348' },
        { name: 'Amazon Pay', icon: SiAmazon, color: '#FF9900' }
      ],
      description: 'Multi-vendor marketplace with 50+ sellers, inventory sync, and commission system',
      metrics: {
        vendors: '50+',
        products: '10K+',
        transactions: '15K/mo'
      }
    },
    {
      title: 'B2B Wholesale Platform',
      image: 'https://images.unsplash.com/photo-1556742502-ec7c0e9f34b1?w=800&auto=format&fit=crop',
      technologies: [
        { name: 'Magento', icon: SiMagento, color: '#ee672f' },
        { name: 'Stripe', icon: SiStripe, color: '#635bff' },
        { name: 'Apple Pay', icon: SiApple, color: '#000000' }
      ],
      description: 'Enterprise B2B platform with bulk ordering, custom pricing, and quote system',
      metrics: {
        clients: '200+',
        orders: '1K/mo',
        avgOrder: '$25K'
      }
    }
  ];

  const testimonials = [
    {
      text: "Our online sales increased by 300% after launching the new e-commerce platform. The team delivered exactly what we needed.",
      author: "Maria Rodriguez",
      company: "Fashion Retailer",
      rating: 5
    },
    {
      text: "The e-commerce solution is robust, secure, and easy to manage. Our customers love the seamless shopping experience.",
      author: "James Wilson",
      company: "Electronics Store",
      rating: 5
    }
  ];

  const ecommerceTypes = [
    {
      title: 'B2B E-commerce',
      description: 'Business-to-business platforms with bulk ordering and custom pricing',
      icon: FaUsers
    },
    {
      title: 'B2C E-commerce',
      description: 'Consumer-focused online stores with optimized shopping experiences',
      icon: FaStore
    },
    {
      title: 'Marketplace',
      description: 'Multi-vendor platforms connecting buyers and sellers',
      icon: FaGlobe
    },
    {
      title: 'Subscription Commerce',
      description: 'Recurring billing and subscription management systems',
      icon: FaBox
    }
  ];

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <meta name="keywords" content="e-commerce development, online store, shopify, woocommerce, magento, payment integration, mobile commerce, analytics, marketplace, b2b, b2c, 2025" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={seoUrl} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:url" content={seoUrl} />
        <meta property="og:image" content={seoImage} />
        <meta property="og:site_name" content="App Developer Pro" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={seoImage} />
        <meta name="twitter:site" content="@appdeveloperpro" />
        {/* Schema.org Markup */}
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>
      <div className="ecommerce-solutions">
      {/* Hero Section */}
      <section className="ecommerce-solutions__hero">
        <div className="ecommerce-solutions__hero-content">
          <motion.div
            className="ecommerce-solutions__hero-text"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="ecommerce-solutions__hero-title">
              Complete <span className="ecommerce-solutions__hero-highlight">E-commerce</span> Solutions
            </h1>
            <p className="ecommerce-solutions__hero-subtitle">
              Build powerful online stores that drive sales and grow your business. From custom e-commerce development to platform integrations, we deliver complete solutions.
            </p>
            <div className="ecommerce-solutions__hero-buttons">
              <Link to="/contact" className="ecommerce-solutions__hero-btn ecommerce-solutions__hero-btn--primary">
                Start Your Store <FaArrowRight />
              </Link>
              <Link to="/portfolio" className="ecommerce-solutions__hero-btn ecommerce-solutions__hero-btn--secondary">
                View E-commerce Projects
              </Link>
            </div>
          </motion.div>
          <motion.div
            className="ecommerce-solutions__hero-visual"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="ecommerce-solutions__hero-store">
              <div className="ecommerce-solutions__hero-browser">
                <div className="ecommerce-solutions__hero-browser-header">
                  <div className="ecommerce-solutions__hero-browser-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <div className="ecommerce-solutions__hero-browser-url">yourstore.com</div>
                </div>
                <div className="ecommerce-solutions__hero-browser-content">
                  <div className="ecommerce-solutions__hero-nav">
                    <div className="ecommerce-solutions__hero-logo">
                      <FaStore />
                    </div>
                    <div className="ecommerce-solutions__hero-cart">
                      <FaShoppingCart />
                      <span>3</span>
                    </div>
                  </div>
                  <div className="ecommerce-solutions__hero-products">
                    <div className="ecommerce-solutions__hero-product">
                      <div className="ecommerce-solutions__hero-product-image"></div>
                      <div className="ecommerce-solutions__hero-product-info">
                        <h4>Product Name</h4>
                        <p>$99.99</p>
                      </div>
                    </div>
                    <div className="ecommerce-solutions__hero-product">
                      <div className="ecommerce-solutions__hero-product-image"></div>
                      <div className="ecommerce-solutions__hero-product-info">
                        <h4>Product Name</h4>
                        <p>$149.99</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="ecommerce-solutions__features">
        <div className="ecommerce-solutions__section-header">
          <motion.h2
            className="ecommerce-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            E-commerce Development Services
          </motion.h2>
          <motion.p
            className="ecommerce-solutions__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Everything you need to build a successful online business
          </motion.p>
        </div>
        <div className="ecommerce-solutions__features-grid">
          {features.map((feature, index) => {
            const FeatureIcon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                className="ecommerce-solutions__feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="ecommerce-solutions__feature-header">
                  <div className="ecommerce-solutions__feature-icon">
                    <FeatureIcon />
                  </div>
                  <h3 className="ecommerce-solutions__feature-title">{feature.title}</h3>
                </div>
                <p className="ecommerce-solutions__feature-description">{feature.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Platforms Section */}
      <section className="ecommerce-solutions__platforms">
        <motion.div
          className="ecommerce-solutions__section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="ecommerce-solutions__section-title">Platforms & Integrations</h2>
          <p className="ecommerce-solutions__section-subtitle">
            We work with leading e-commerce platforms and payment providers
          </p>
        </motion.div>
        <div className="ecommerce-solutions__platforms-grid">
          {platforms.map((platform, index) => {
            const PlatformIcon = platform.icon;
            return (
              <motion.div
                key={platform.name}
                className="ecommerce-solutions__platform-item"
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <div 
                  className="ecommerce-solutions__platform-icon"
                  style={{ '--platform-color': platform.color } as React.CSSProperties}
                >
                  <PlatformIcon />
                </div>
                <h3 className="ecommerce-solutions__platform-name">{platform.name}</h3>
                <p className="ecommerce-solutions__platform-description">{platform.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* E-commerce Types Section */}
      <section className="ecommerce-solutions__types">
        <div className="ecommerce-solutions__section-header">
          <motion.h2
            className="ecommerce-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Types of E-commerce Solutions
          </motion.h2>
        </div>
        <div className="ecommerce-solutions__types-grid">
          {ecommerceTypes.map((type, index) => {
            const TypeIcon = type.icon;
            return (
              <motion.div
                key={type.title}
                className="ecommerce-solutions__type-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <div className="ecommerce-solutions__type-icon">
                  <TypeIcon />
                </div>
                <h3 className="ecommerce-solutions__type-title">{type.title}</h3>
                <p className="ecommerce-solutions__type-description">{type.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="ecommerce-solutions__benefits">
        <div className="ecommerce-solutions__section-header">
          <motion.h2
            className="ecommerce-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Why Choose Our E-commerce Solutions
          </motion.h2>
        </div>
        <div className="ecommerce-solutions__benefits-grid">
          {benefits.map((benefit, index) => {
            const BenefitIcon = benefit.icon;
            return (
              <motion.div
                key={benefit.title}
                className="ecommerce-solutions__benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="ecommerce-solutions__benefit-icon">
                  <BenefitIcon />
                </div>
                <h3 className="ecommerce-solutions__benefit-title">{benefit.title}</h3>
                <p className="ecommerce-solutions__benefit-description">{benefit.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Completed Projects Section */}
      <section className="ecommerce-solutions__process">
        <div className="ecommerce-solutions__section-header">
          <motion.h2
            className="ecommerce-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Completed Latest Projects
          </motion.h2>
          <motion.p
            className="ecommerce-solutions__section-subtitle"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            Real e-commerce stores we've built for real businesses
          </motion.p>
        </div>
        <div 
          className="ecommerce-solutions__projects-container"
          style={{
            display: 'flex',
            flexDirection: 'row' as const,
            flexWrap: 'nowrap' as const,
            gap: '30px',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '0 20px'
          }}
        >
          {completedProjects.map((project, index) => (
            <motion.div
              key={project.title}
              className="ecommerce-solutions__project-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -10 }}
              style={{
                flex: '1 1 calc(33.333% - 20px)',
                minWidth: '300px',
                maxWidth: 'calc(33.333% - 20px)',
                background: 'white',
                borderRadius: '15px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
              }}
            >
              <div 
                className="ecommerce-solutions__project-image"
                style={{
                  position: 'relative',
                  height: '250px',
                  overflow: 'hidden'
                }}
              >
                <img 
                  src={project.image} 
                  alt={project.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div 
                  className="ecommerce-solutions__project-overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(to top, rgba(103, 58, 183, 0.9), transparent)',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                    padding: '20px',
                    gap: '8px'
                  }}
                >
                  {project.technologies.map((tech) => {
                    const TechIcon = tech.icon;
                    return (
                      <span
                        key={tech.name}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          padding: '5px 12px',
                          background: 'rgba(255,255,255,0.95)',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: tech.color
                        }}
                      >
                        <TechIcon /> {tech.name}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div 
                className="ecommerce-solutions__project-content"
                style={{
                  padding: '25px'
                }}
              >
                <h3 
                  style={{
                    fontSize: '22px',
                    fontWeight: '700',
                    marginBottom: '12px',
                    color: '#2c3e50'
                  }}
                >
                  {project.title}
                </h3>
                <p 
                  style={{
                    fontSize: '15px',
                    lineHeight: '1.6',
                    color: '#555',
                    marginBottom: '20px'
                  }}
                >
                  {project.description}
                </p>
                <div 
                  className="ecommerce-solutions__project-metrics"
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: '20px',
                    borderTop: '1px solid #eee'
                  }}
                >
                  {Object.entries(project.metrics).map(([key, value]) => (
                    <div 
                      key={key}
                      style={{
                        textAlign: 'center'
                      }}
                    >
                      <div 
                        style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: '#673ab7',
                          marginBottom: '5px'
                        }}
                      >
                        {value}
                      </div>
                      <div 
                        style={{
                          fontSize: '12px',
                          color: '#888',
                          textTransform: 'capitalize'
                        }}
                      >
                        {key}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="ecommerce-solutions__testimonials">
        <div className="ecommerce-solutions__section-header">
          <motion.h2
            className="ecommerce-solutions__section-title"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Client Success Stories
          </motion.h2>
        </div>
        <div className="ecommerce-solutions__testimonials-grid">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={testimonial.author}
              className="ecommerce-solutions__testimonial-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="ecommerce-solutions__testimonial-quote">
                <FaQuoteLeft />
              </div>
              <div className="ecommerce-solutions__testimonial-stars">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <FaStar key={i} />
                ))}
              </div>
              <p className="ecommerce-solutions__testimonial-text">{testimonial.text}</p>
              <div className="ecommerce-solutions__testimonial-author">
                <strong>{testimonial.author}</strong>
                <span>{testimonial.company}</span>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="ecommerce-solutions__cta">
        <motion.div
          className="ecommerce-solutions__cta-content"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="ecommerce-solutions__cta-title">Ready to Launch Your Online Store?</h2>
          <p className="ecommerce-solutions__cta-text">
            Transform your business with a powerful e-commerce platform. Contact us today for a free consultation and project quote.
          </p>
          <div className="ecommerce-solutions__cta-buttons">
            <Link to="/contact" className="ecommerce-solutions__cta-btn ecommerce-solutions__cta-btn--primary">
              Get Free Quote <FaArrowRight />
            </Link>
            <Link to="/portfolio" className="ecommerce-solutions__cta-btn ecommerce-solutions__cta-btn--secondary">
              View E-commerce Portfolio
            </Link>
          </div>
        </motion.div>
      </section>

      {/* FAQ Section - SEO Boost */}
      <section className="ecommerce-solutions__faq">
        <div className="container">
          <motion.div
            className="ecommerce-solutions__section-header"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Frequently Asked Questions About E-commerce Development</h2>
            <p>Everything you need to know about building successful online stores</p>
          </motion.div>
          <div className="ecommerce-solutions__faq-list">
            <motion.div
              className="ecommerce-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>How much does it cost to build an e-commerce website?</h3>
              <p>As compare to market price, App Developer Pro delivers high performance E-commerce websites at very low cost. E-commerce costs vary by complexity. Basic Shopify/WooCommerce stores start at $1,000-$2,000, custom e-commerce platforms range from $2,000-$3,000, and enterprise marketplace solutions cost $3,000-$5,000+. Factors include product catalog size, payment integrations, custom features, and third-party integrations.</p>
            </motion.div>
            <motion.div
              className="ecommerce-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h3>Which platform is best: Shopify, WooCommerce, or custom?</h3>
              <p>It depends on your needs. Shopify is great for quick launches with lower costs. WooCommerce offers more customization on WordPress. Custom solutions provide unlimited flexibility for unique business models. App Developer Pro analysis your products, volume, features, and budget to recommend the perfect platform for your business.</p>
            </motion.div>
            <motion.div
              className="ecommerce-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>Do you integrate payment gateways and shipping providers?</h3>
              <p>Yes! I integrate all major payment gateways (Stripe, PayPal, Square, Authorize.net) and shipping carriers (FedEx, UPS, USPS, DHL). App Developer Pro handle secure payment processing, automatic tax calculations, real-time shipping rates, and inventory management to ensure smooth operations.</p>
            </motion.div>
            <motion.div
              className="ecommerce-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h3>How long does e-commerce website development take?</h3>
              <p>App Developer Pro is too fast from others, Where timelines vary: Simple Shopify/WooCommerce stores take 3-4 weeks, custom e-commerce platforms take 4-6 weeks, and complex marketplace solutions take 6-12 weeks+. This includes design, development, payment integration, testing, and launch. I provide detailed timelines during planning.</p>
            </motion.div>
            <motion.div
              className="ecommerce-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <h3>Is my e-commerce store secure and PCI compliant?</h3>
              <p>Security is our top priority! App Developer Pro implement SSL certificates, PCI DSS compliance, secure payment processing, data encryption, fraud detection, and regular security audits. Your customers' data is protected with industry-standard security measures and best practices.</p>
            </motion.div>
            <motion.div
              className="ecommerce-solutions__faq-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true }}
            >
              <h3>Can you help with digital marketing and SEO for my store?</h3>
              <p>Absolutely! App Developer Pro offer complete digital marketing services including SEO optimization, Google Shopping ads, Facebook/Instagram advertising, email marketing automation, abandoned cart recovery, and conversion rate optimization. App Developer Pro help drive traffic and maximize your online sales.</p>
            </motion.div>
          </div>
        </div>
      </section>
      </div>
    </>
  );
};

export default EcommerceSolutions;
